import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Table, Card, Button, Space, Input, Tag, message, Switch, Tooltip } from 'antd';
import { PlusOutlined, SearchOutlined, GlobalOutlined } from '@ant-design/icons';
import { getAppVersions, getAppVersionsCount, getGlobalReviewMode } from '../../graphql/queries';
import { setGlobalReviewMode } from '../../graphql/mutation';
import { useNavigate } from 'react-router-dom';
import { getRowLinkProps, withRowLink } from '../../utils/rowLink';
import './AppVersions.css';

const AppVersions = () => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [searchTerm, setSearchTerm] = useState('');

  const { data: globalReviewData } = useQuery(getGlobalReviewMode);
  const [globalReviewEnabled, setGlobalReviewEnabled] = useState(false);
  const [globalReviewLoading, setGlobalReviewLoading] = useState(false);
  const [setGlobalReviewModeMutation] = useMutation(setGlobalReviewMode);

  useEffect(() => {
    if (globalReviewData?.getGlobalReviewMode !== undefined) {
      setGlobalReviewEnabled(globalReviewData.getGlobalReviewMode);
    }
  }, [globalReviewData]);

  const handleGlobalReviewToggle = async (enabled) => {
    setGlobalReviewLoading(true);
    try {
      await setGlobalReviewModeMutation({ variables: { enabled } });
      setGlobalReviewEnabled(enabled);
      message.success(enabled ? 'Global review mode enabled.' : 'Global review mode disabled.');
    } catch (err) {
      message.error('Failed to update global review mode: ' + err.message);
    } finally {
      setGlobalReviewLoading(false);
    }
  };

  const { loading, data, refetch } = useQuery(getAppVersions, {
    variables: {
      page: pagination.current,
      pageSize: pagination.pageSize,
    },
  });

  const { data: countData } = useQuery(getAppVersionsCount);

  const handleRowClick = (record) => {
    navigate(`/app-versions/${record.id}`);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '200px',
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: '150px',
    },
    {
      title: 'Review Mode',
      dataIndex: 'reviewMode',
      key: 'reviewMode',
      width: '120px',
      render: (reviewMode) => (
        <Tag color={reviewMode ? 'orange' : 'green'}>
          {reviewMode ? 'Review' : 'Normal'}
        </Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '200px',
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: '200px',
      render: (date) => new Date(date).toLocaleString(),
    },
  ];

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    });
  };

  // Filter data based on search term
  const filteredData = (data?.getAppVersions || []).filter(appVersion =>
    appVersion.version.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="app-versions-container">
      <Card>
        <div className="page-header">
          <div className="header-content">
            <h1>App Versions</h1>
            <p>Manage app versions and review modes</p>
          </div>
          <Space>
            <Tooltip title={globalReviewEnabled ? 'Global review mode is ON — all users are in review mode regardless of app version' : 'Global review mode is OFF — review mode follows per-version settings'}>
              <Space>
                <GlobalOutlined />
                <span style={{ fontSize: 14 }}>Global Review Mode</span>
                <Switch
                  checked={globalReviewEnabled}
                  loading={globalReviewLoading}
                  onChange={handleGlobalReviewToggle}
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                />
              </Space>
            </Tooltip>
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => navigate('/app-versions/create')}
            >
              Create App Version
            </Button>
          </Space>
        </div>

        <div className="filters-section">
          <Space size="large" wrap>
            <div className="filter-item">
              <Input
                placeholder="Search by version..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: 250 }}
                prefix={<SearchOutlined />}
                allowClear
              />
            </div>
          </Space>
        </div>
        
        {globalReviewEnabled && (
          <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6, color: '#d46b08' }}>
            Global review mode is active — all versions are currently in review mode. Per-version settings are disabled.
          </div>
        )}
        <Table
          columns={withRowLink(columns, (record) => `/app-versions/${record.id}`, { disabled: globalReviewEnabled })}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          style={globalReviewEnabled ? { opacity: 0.5, pointerEvents: 'none' } : {}}
          pagination={{
            ...pagination,
            total: countData?.getAppVersionsCount || 0,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} app versions`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            ...getRowLinkProps(`/app-versions/${record.id}`, () => handleRowClick(record), {
              shouldIgnore: () => globalReviewEnabled,
            }),
            style: { cursor: globalReviewEnabled ? 'not-allowed' : 'pointer' }
          })}
        />
      </Card>
    </div>
  );
};

export default AppVersions;
